import { useEffect, useRef } from 'react';

// REVALIDATE — for state the SERVER changes without the user doing anything.
//
// The dashboard loads once and then renders that snapshot until something on the
// page triggers a reload. That is fine for data only the user changes, and wrong
// for data the platform changes on its own:
//
//   • `campaigns.status` — the health monitor auto-pauses a campaign on systemic
//     AI malfunction. The user did not do it, gets no signal, and the page keeps
//     showing "active" indefinitely.
//   • contact counters and progress — they move as the dialer works.
//
// A stale screen here is not a cosmetic problem. It says a campaign is running
// when the platform has stopped it, which is the same class of failure as a
// counter reading 0 against 243 real contacts: a cached value drifting from the
// truth, with nothing on screen admitting it might be stale.
//
// Deliberately dependency-free rather than pulling in a data-fetching library —
// this is one small hook against an existing hand-rolled `load()`.
//
// Behaviour:
//   • polls every `intervalMs` while the tab is VISIBLE;
//   • stops entirely while hidden (a backgrounded tab must not keep hitting the
//     API, and there is no one there to see the result);
//   • revalidates immediately on regaining visibility or focus — the common case
//     is someone coming back to a tab they left open, which is exactly when the
//     snapshot is most stale;
//   • never overlaps: a tick is skipped while one is already in flight.
//
// `fn` is called with no arguments and may be async. Errors are swallowed —
// a failed background refresh must not replace good data with an error banner.
export default function useRevalidate(fn, { intervalMs = 30_000, enabled = true } = {}) {
  // Keep the latest callback in a ref so a re-created `load` (it closes over
  // state on every render) doesn't tear down and restart the interval.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const inFlight = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;

    const run = async () => {
      if (inFlight.current) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight.current = true;
      try {
        await fnRef.current?.();
      } catch {
        // Background refresh: stay quiet. The user's last good view survives.
      } finally {
        inFlight.current = false;
      }
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(run, intervalMs);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        run();
        start();
      } else {
        stop();
      }
    };

    if (typeof document === 'undefined' || document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', run);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', run);
    };
  }, [intervalMs, enabled]);
}
